import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePermissions } from "../context/PermissionContext";
import { turkishIncludes } from "../utils/formatters";
import { MENU_GROUPS } from "./Sidebar";
import "./FirmPicker.css";
import "../pages/PurchaseOrders.css";

/**
 * Ekran seçici — firma/tedarikçi seçicilerinin (fp-*) kardeşi, tekli seçim.
 * Kaynak menünün kendisi (MENU_GROUPS) + menüde olmayan iki yüzey ("Diğer").
 * Geliştirme talebinde "hangi ekranla ilgili" sorusu için kullanılır; null = Genel.
 */

export interface ScreenOption { code: string; title: string; description: string; group: string; path: string | null }

const OTHER_GROUP = "Diğer";
/** Menüde olmayan ama talep konusu olabilen yüzeyler; sunucu da bu iki kodu kabul eder. */
export const SCREEN_EXTRAS: ScreenOption[] = [
  { code: "notifications", title: "Bildirimler", description: "Zil, bildirim listesi ve e-posta tercihleri", group: OTHER_GROUP, path: "/notifications" },
  { code: "login", title: "Giriş ve şifre", description: "Oturum açma, şifre değiştirme ve sıfırlama", group: OTHER_GROUP, path: null },
];
/** Menü sırasıyla tüm seçilebilir ekranlar ("Yeni Sipariş" bir ekran değil, Siparişler'in eylemi: atlanır). */
export const SCREEN_OPTIONS: ScreenOption[] = (() => {
  const out: ScreenOption[] = [];
  MENU_GROUPS.forEach((g) => g.items.forEach((i) => {
    if (i.id !== "new-order") out.push({ code: i.id, title: i.title, description: i.description, group: g.groupTitle, path: i.path });
  }));
  return out.concat(SCREEN_EXTRAS);
})();
export const screenOption = (code?: string | null): ScreenOption | undefined => (code ? SCREEN_OPTIONS.find((o) => o.code === code) : undefined);
/** "Ekrana git" hedefi: yalnız menüde yolu olan ekranlar. Çağıran ayrıca hasScreen(code) bakmalı. */
export const screenPath = (code?: string | null): string | null => {
  const o = screenOption(code);
  return o && o.group !== OTHER_GROUP ? o.path : null;
};

const STOP = ["ve", "ile", "&"];
/** "Mizan ve Nakit Akış" → "MN", "Ürün Ağacı (BOM)" → "ÜA". */
export const screenInitials = (title: string) =>
  (title || "").replace(/\(.*?\)/g, " ").split(/\s+/).filter((w) => w && STOP.indexOf(w.toLocaleLowerCase("tr-TR")) < 0)
    .slice(0, 2).map((w) => w[0]).join("").toLocaleUpperCase("tr-TR") || "?";
const GROUP_HUE: Record<string, number> = {};
MENU_GROUPS.forEach((g, i) => { GROUP_HUE[g.groupTitle] = (210 + i * 47) % 360; });
const hueOf = (o?: ScreenOption) => (o ? GROUP_HUE[o.group] ?? 220 : 220);

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>,
  screen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
};

const matches = (o: ScreenOption, q: string) => {
  const terms = q.trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = [o.title, o.description];
  return terms.every((t) => hay.some((h) => turkishIncludes(h, t)));
};
const NULL_KEY = "__null";
let seq = 0;

export interface ScreenPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Seçili ekran kodu (null = Genel). */
  selected?: string | null;
  onSelect: (code: string | null, name: string | null) => void;
  /** Verilirse sabit ilk satır bu etiketle "ekran yok" olur (değer null). */
  nullLabel?: string;
  title?: string;
}

export interface ScreenPickerFieldProps {
  value: string | null;
  onChange: (code: string | null, name: string | null) => void;
  nullLabel: string;
  /** Kayıtlı ad kopyası (talebin screenName'i): kod artık listede yoksa bu gösterilir. */
  screenName?: string | null;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
  title?: string;
  /** Alanın görünür etiketinin id'si: ekran okuyucu düğmeyi "Ekran" alanı olarak okusun. */
  labelledBy?: string;
}

export const ScreenPickerModal: React.FC<ScreenPickerModalProps> = ({ open, onClose, selected, onSelect, nullLabel, title = "Ekran seç" }) => {
  const uid = useRef(`scp${++seq}`).current;
  const { hasScreen, has } = usePermissions();
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState<string>("");
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Talepleri yönetenler her ekranı seçebilir; diğerleri yalnız erişebildiklerini (seçili olan her durumda görünür)
  const seeAll = has("dev-requests:update");
  const source = useMemo(
    () => SCREEN_OPTIONS.filter((o) => o.group === OTHER_GROUP || seeAll || hasScreen(o.code) || o.code === selected),
    [seeAll, hasScreen, selected]);
  const filtered = useMemo(() => source.filter((o) => matches(o, q)), [source, q]);
  const keys = useMemo(() => (nullLabel ? [NULL_KEY] : []).concat(filtered.map((o) => o.code)), [nullLabel, filtered]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    const t = window.setTimeout(() => searchRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);
  // İmleç satır anahtarıyla izlenir: aramada ilk eşleşme, boş aramada seçili satır
  useEffect(() => {
    if (!open) return;
    if (q.trim()) setCursor(filtered.length ? filtered[0].code : keys[0] || "");
    else setCursor(selected && keys.indexOf(selected) >= 0 ? selected : keys[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);
  useEffect(() => { if (cursor) listRef.current?.querySelector<HTMLElement>(`[data-key="${cursor}"]`)?.scrollIntoView({ block: "nearest" }); }, [cursor]);

  const pick = (o: ScreenOption | null) => { onSelect(o ? o.code : null, o ? o.title : null); onClose(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); onClose(); return; }
    if (e.target !== searchRef.current || !keys.length) return;
    const i = keys.indexOf(cursor);
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor(keys[Math.min(keys.length - 1, i + 1)]); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor(keys[Math.max(0, i - 1)]); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (cursor === NULL_KEY) return pick(null);
      const o = filtered.find((x) => x.code === cursor);
      if (o) pick(o);
    }
  };

  if (!open) return null;
  const groups: { title: string; items: ScreenOption[] }[] = [];
  filtered.forEach((o) => {
    const g = groups[groups.length - 1];
    if (g && g.title === o.group) g.items.push(o); else groups.push({ title: o.group, items: [o] });
  });
  return createPortal(
    <div className="fp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onClick={(e) => e.stopPropagation()} onPaste={(e) => e.stopPropagation()} onKeyDown={onKey}>
      <div className="fp-modal" role="dialog" aria-modal="true" aria-labelledby={`${uid}-t`} onMouseDown={(e) => e.stopPropagation()}>
        <header className="fp-head">
          <div>
            <span className="fp-kicker">Ekran</span>
            <h2 id={`${uid}-t`}>{title}</h2>
            <p className="fp-sub">Talebin ilgili olduğu ekran · {source.length} ekran</p>
          </div>
          <button type="button" className="fp-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </header>
        <div className="fp-tools">
          <div className="fp-search">{I.search}
            <input ref={searchRef} placeholder="Ekran adı ya da açıklaması ara" aria-label="Ekran ara" value={q} onChange={(e) => setQ(e.target.value)} />
            {q && <button type="button" className="fp-ib" aria-label="Temizle" onClick={() => { setQ(""); searchRef.current?.focus(); }}>{I.close}</button>}
          </div>
        </div>
        <div className="fp-list" ref={listRef} role="listbox" aria-label="Ekranlar">
          {nullLabel && (
            <button type="button" role="option" aria-selected={!selected} tabIndex={-1} className={`fp-row null ${cursor === NULL_KEY ? "cur" : ""} ${!selected ? "sel" : ""}`} data-key={NULL_KEY} onMouseEnter={() => setCursor(NULL_KEY)} onClick={() => pick(null)}>
              <span className="fp-mark null">{I.screen}</span>
              <span className="fp-text"><b>{nullLabel}</b><small>Belirli bir ekranla ilgili değil ya da hangisi olduğu bilinmiyor</small></span>
              {!selected && <span className="fp-check">{I.check}</span>}
            </button>
          )}
          {filtered.length === 0 && (
            <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>"{q}" için ekran bulunamadı</b>{nullLabel ? `Aramayı değiştirin ya da "${nullLabel}" seçin.` : "Aramayı değiştirin."}</div>
          )}
          {groups.map((g) => (
            <React.Fragment key={g.title}>
              <div className="fp-group" role="presentation">{g.title}</div>
              {g.items.map((o) => {
                const sel = o.code === selected;
                return (
                  <button type="button" key={o.code} role="option" aria-selected={sel} tabIndex={-1} className={`fp-row ${cursor === o.code ? "cur" : ""} ${sel ? "sel" : ""}`} data-key={o.code} onMouseEnter={() => setCursor(o.code)} onClick={() => pick(o)}>
                    <span className="fp-mark" style={{ ["--fp-hue" as any]: hueOf(o) }}>{screenInitials(o.title)}</span>
                    <span className="fp-text"><b><span className="fp-name">{o.title}</span></b><small>{o.description}</small></span>
                    {sel && <span className="fp-check">{I.check}</span>}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
          {filtered.length > 0 && !!q && filtered.length !== source.length && <div className="fp-count">{filtered.length} / {source.length} ekran</div>}
        </div>
        <footer className="fp-foot">
          <span className="fp-keys"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> seç · <kbd>Esc</kbd> vazgeç</span>
          <span className="fp-actions"><button type="button" className="fp-btn" onClick={onClose}>Vazgeç</button></span>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export const ScreenPickerField: React.FC<ScreenPickerFieldProps> = ({ value, onChange, nullLabel, screenName, invalid, disabled, id, title, labelledBy }) => {
  const autoId = useId();
  const ctlId = id ?? autoId;
  const [open, setOpen] = useState(false);
  const ctl = useRef<HTMLButtonElement>(null);
  const o = screenOption(value);
  const name = o ? o.title : value ? screenName || value : null;
  const close = () => { setOpen(false); window.setTimeout(() => ctl.current?.focus(), 0); };
  return (
    <div className={`fp-field form ${disabled ? "disabled" : ""} ${value ? "has" : ""} ${invalid ? "po-invalid" : ""}`}>
      <button ref={ctl} type="button" id={ctlId} className="fp-control" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} title={name || undefined}
        aria-labelledby={labelledBy ? `${labelledBy} ${ctlId}` : undefined} onClick={() => setOpen(true)}>
        {value ? <span className="fp-mark" style={{ ["--fp-hue" as any]: hueOf(o) }}>{screenInitials(name || "")}</span> : <span className="fp-mark null">{I.screen}</span>}
        <span className="fp-text">
          {value ? <><b>{name}</b><small>{o ? `${o.group} · ${o.description}` : "Kayıtlı ekran"}</small></>
            : <b className="fp-placeholder">{nullLabel}</b>}
        </span>
        <span className="fp-chev">{I.chev}</span>
      </button>
      {!!value && !disabled && <button type="button" className="fp-clear" aria-label="Seçimi kaldır" title="Seçimi kaldır" onClick={() => onChange(null, null)}>{I.close}</button>}
      <ScreenPickerModal open={open} onClose={close} selected={value} onSelect={onChange} nullLabel={nullLabel} title={title} />
    </div>
  );
};

export default ScreenPickerField;
